// The volume controls, and the only place the player meets the audio engine.
//
// **Self-contained on purpose: it takes no props.** It holds its own prefs,
// pushes them into the engine itself, and knows nothing about the panel it is
// drawn in — so moving it out of the front-door gear and into a proper settings
// surface is a line of JSX in two files rather than a refactor. The placement
// is temporary; the component is not.
//
// It is also the whole of what makes a mute button safe: mute is a *field*,
// which the engine turns into a master of nought, so the stored level is
// untouched and unmuting is the same call with the same number. Nothing here
// zeroes a slider to silence anything.

import { useEffect, useState } from "react";

import { sound } from "src/client/ai/audio/engine";
import {
  HoverCard,
  SectionTitle,
  SettingRow,
  Slider,
  StatCounter,
  styled,
  Toggle
} from "./kit";
import { type SoundPrefs, useSoundPrefs } from "./soundPrefs";

/**
 * The buses in the order they matter to somebody turning one down.
 *
 * Master first because it is the one control most people ever touch, then the
 * two beds, then the two that carry information. Labelled for what a player
 * hears rather than for what the bus is called: `sfx` is "the board" and `ui`
 * is "the chrome", and neither of those words is on the tin.
 */
type LevelKey = Exclude<keyof SoundPrefs, "muted">;

const LEVELS: readonly { key: LevelKey; label: string; detail: string }[] = [
  { key: "master", label: "Master", detail: "Everything at once." },
  { key: "music", label: "Music", detail: "The score." },
  { key: "ambience", label: "Ambience", detail: "The room the board is in." },
  { key: "sfx", label: "Effects", detail: "Pieces, machines, belts." },
  { key: "ui", label: "Interface", detail: "Buttons, panels, refusals." }
];

/**
 * A slider runs 0..100 and a gain runs 0..1.
 *
 * Whole percent rather than a fraction with a step: a slider whose value is a
 * float accumulates rounding on every drag, and a number printed beside the
 * control has to be one a person could read back and repeat.
 */
const SLIDER_MAX = 100;

/** The slider and its number, on the right of the row it labels. */
const LevelControl = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  width: 220
}));

/**
 * **Not disabled while muted.** Setting the levels you want before you take the
 * mute off is a legitimate thing to do, and five controls that all stop
 * responding read as the settings being broken rather than as the sound being
 * off. Faded, so it says which it is.
 */
const Levels = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "muted"
})<{ muted?: boolean }>(({ muted }) => ({ opacity: muted ? 0.55 : 1 }));

/** The label side of a row. Its own, because `pause/text` is the plate's voice. */
const Label = styled("span")({ cursor: "help" });

export function SoundSettings(): React.ReactElement {
  const [prefs, setPrefs] = useSoundPrefs();
  // The panel a level's hover card hangs off, rather than the label it names:
  // these are stacked rows, so a card beside the trigger would land on the
  // slider the row is about (the kit's rule 4).
  const [panel, setPanel] = useState<HTMLElement | null>(null);

  // The one place settings reach the graph. Every field is in the object, so
  // the dep array is the object — `applyPrefs` writes all five buses and mute
  // at once, and listing the fields would be six chances to forget one.
  useEffect(() => sound.applyPrefs(prefs), [prefs]);

  // Written into a typed patch rather than as a computed key, or the object
  // literal widens to a string index of `number` and stops being assignable to
  // a `Partial` whose sixth field is a boolean.
  const setLevel = (key: LevelKey, value: number) => {
    const patch: Partial<SoundPrefs> = {};
    patch[key] = value;
    setPrefs(patch);
  };

  return (
    <div ref={setPanel}>
      <SectionTitle>Sound</SectionTitle>

      <SettingRow>
        <span>Mute</span>
        <Toggle
          data-testid="sound-mute"
          checked={prefs.muted}
          onChange={() => setPrefs({ muted: !prefs.muted })}
          aria-label="Mute"
        />
      </SettingRow>

      <Levels muted={prefs.muted}>
        {LEVELS.map(({ key, label, detail }) => (
          <SettingRow key={key} data-testid={`sound-${key}`}>
            <HoverCard card={detail} placement="right-start" beside={panel}>
              <Label>{label}</Label>
            </HoverCard>
            <LevelControl>
              <Slider
                size="small"
                value={Math.round(prefs[key] * SLIDER_MAX)}
                min={0}
                max={SLIDER_MAX}
                step={1}
                aria-label={label}
                valueLabelDisplay="off"
                onChange={(_, value) => setLevel(key, (value as number) / SLIDER_MAX)}
              />
              {/*
                The number is printed because a slider without one is a control
                you set by feel and then cannot repeat.
              */}
              <StatCounter
                data-testid={`sound-${key}-value`}
                style={{ fontSize: 13, minWidth: 24, textAlign: "right" }}
              >
                {Math.round(prefs[key] * SLIDER_MAX)}
              </StatCounter>
            </LevelControl>
          </SettingRow>
        ))}
      </Levels>
    </div>
  );
}
